import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faPaperPlane,
  faTriangleExclamation,
  faCircleInfo,
  faWandMagicSparkles,
  faEye,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./ProgressReportSection.module.css";

/**
 * Relatório de progresso do aluno — visível no perfil dele, no painel admin.
 *
 * A tela existe nesta forma por causa da reversão da primeira versão: antes
 * era um botão de ação solto, sem nenhuma indicação de quando (ou se) o aluno
 * já havia recebido. Aqui o ÚLTIMO ENVIO é a informação principal, e o botão
 * é consequência dela — não o contrário.
 *
 * Props:
 *   studentId   – id do aluno
 *   studentName – nome, só para o texto de confirmação
 */
const AVISO_REENVIO_DIAS = 7;

function formatarData(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatarVolume(kg) {
  if (!kg) return "0 kg";
  return kg >= 1000 ? `${(kg / 1000).toFixed(1).replace(".", ",")} t` : `${Math.round(kg)} kg`;
}

export default function ProgressReportSection({ studentId, studentName }) {
  // Carrega só quando o admin abre a seção. Antes buscava no mount, o que
  // gastava uma requisição por perfil aberto — inclusive quando o admin só
  // queria trocar uma senha e nem ia enviar relatório.
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [previaHtml, setPreviaHtml] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const confirmarRef = useRef(null);

  const carregar = useCallback(async () => {
    if (!studentId) return;
    setCarregando(true);
    setErro("");
    try {
      const { data } = await api.get(`/users/${studentId}/progress-report/preview`);
      setDados(data);
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível carregar o relatório.");
    } finally {
      setCarregando(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (aberto && !dados && !carregando) carregar();
  }, [aberto, dados, carregando, carregar]);

  // Move o foco pro botão de confirmar quando o bloco aparece. Sem isso,
  // quem navega por teclado ou leitor de tela não percebe que surgiu um passo
  // novo entre o clique e o envio.
  useEffect(() => {
    if (confirmando && confirmarRef.current) confirmarRef.current.focus();
  }, [confirmando]);

  const verPrevia = async () => {
    setCarregandoPrevia(true);
    setErro("");
    try {
      const { data } = await api.get(
        `/users/${studentId}/progress-report/preview`,
        { params: { include_html: true } }
      );
      setPreviaHtml(data?.html || null);
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível gerar a prévia.");
    } finally {
      setCarregandoPrevia(false);
    }
  };

  const enviar = async () => {
    setEnviando(true);
    setErro("");
    setSucesso("");
    try {
      await api.post(`/users/${studentId}/progress-report`, {});
      setSucesso("Relatório enviado.");
      setConfirmando(false);
      setPreviaHtml(null);
      setDados(null);      // força recarregar e já mostrar o novo "último envio"
      await carregar();
    } catch (e) {
      setErro(e?.response?.data?.detail || "Falha ao enviar o relatório.");
    } finally {
      setEnviando(false);
    }
  };

  const cabecalho = (
    <button
      type="button"
      className={styles.header}
      onClick={() => setAberto((v) => !v)}
      aria-expanded={aberto}
    >
      <span className={styles.title}>
        <FontAwesomeIcon icon={faChartLine} /> Relatório de progresso
      </span>
      <FontAwesomeIcon icon={aberto ? faChevronUp : faChevronDown} className={styles.chevron} />
    </button>
  );

  if (!aberto) {
    return <div className={styles.section}>{cabecalho}</div>;
  }

  if (carregando || !dados) {
    return (
      <div className={styles.section}>
        {cabecalho}
        <p className={styles.helper} role="status">
          {erro || "Carregando…"}
        </p>
      </div>
    );
  }

  const { kpis, last_sent: ultimo, days_since_last_sent: diasDesde, email_configured: emailOk } = dados;
  const atual = kpis?.current || {};
  const periodo = kpis?.period || {};
  const semDados = !kpis?.has_data;
  const reenvioRecente = diasDesde != null && diasDesde < AVISO_REENVIO_DIAS;

  return (
    <div className={styles.section}>
      {cabecalho}

      <p className={styles.helper}>
        Consolidado de {formatarData(periodo.from)} até hoje, enviado por email para o aluno.
      </p>

      {/* Último envio primeiro: é a informação que faltava e derrubou a v1 */}
      <div className={styles.lastSent}>
        {ultimo ? (
          <>
            <div className={styles.lastSentMain}>
              Último envio em <strong>{formatarData(ultimo.sent_at)}</strong>
              {diasDesde != null && (
                <span className={styles.muted}>
                  {" "}({diasDesde === 0 ? "hoje" : `há ${diasDesde} dia${diasDesde === 1 ? "" : "s"}`})
                </span>
              )}
            </div>
            <div className={styles.lastSentMeta}>
              {ultimo.automatic ? "Envio automático · " : ultimo.sent_by ? `por ${ultimo.sent_by} · ` : ""}
              cobria {formatarData(ultimo.period_start)} a {formatarData(ultimo.period_end)}
              {ultimo.workouts != null && ` · ${ultimo.workouts} treino${ultimo.workouts === 1 ? "" : "s"}`}
            </div>
          </>
        ) : (
          <div className={styles.lastSentMain}>
            <span className={styles.muted}>Nenhum relatório enviado ainda.</span>
          </div>
        )}
      </div>

      <div className={styles.kpiGrid}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Treinos</span>
          <strong className={styles.kpiValue}>{atual.workouts ?? 0}</strong>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Volume</span>
          <strong className={styles.kpiValue}>{formatarVolume(atual.volume_kg)}</strong>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Sequência</span>
          <strong className={styles.kpiValue}>{atual.streak ?? 0}d</strong>
        </div>
      </div>

      <div className={styles.infoBox}>
        <FontAwesomeIcon icon={dados.insight_text ? faWandMagicSparkles : faCircleInfo} />
        <span>
          {dados.insight_text
            ? "O email vai incluir o insight que o aluno gerou."
            : "Sem insight aproveitável - o email vai só com os números."}
        </span>
      </div>

      {semDados && (
        <div className={styles.warnBox}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>Nenhum treino no período. O email sairá com um convite pra recomeçar.</span>
        </div>
      )}

      {!emailOk && (
        <div className={styles.warnBox}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>Envio de email não configurado no servidor.</span>
        </div>
      )}

      {/* role="status"/"alert": envio é irreversível, então o resultado precisa
          ser anunciado por leitor de tela, não só aparecer na tela. */}
      {erro && <p className={styles.error} role="alert">{erro}</p>}
      {sucesso && <p className={styles.success} role="status">{sucesso}</p>}

      {/* Prévia do email REAL. Enviar não tem desfazer — sem ver a peça, o
          admin confirma no escuro: os 3 KPIs acima não representam um email
          com scorecards, dois gráficos e comparativo. */}
      {previaHtml ? (
        <div className={styles.previewWrap}>
          <div className={styles.previewBar}>
            <span>Prévia do email</span>
            <button type="button" className={styles.linkBtn} onClick={() => setPreviaHtml(null)}>
              Fechar prévia
            </button>
          </div>
          <iframe
            title="Prévia do relatório de progresso"
            srcDoc={previaHtml}
            className={styles.previewFrame}
            sandbox=""
          />
        </div>
      ) : (
        <button
          type="button"
          className={`${styles.btn} ${styles.btnBlock}`}
          onClick={verPrevia}
          disabled={carregandoPrevia}
        >
          <FontAwesomeIcon icon={faEye} /> {carregandoPrevia ? "Gerando prévia…" : "Ver prévia do email"}
        </button>
      )}

      {confirmando ? (
        <div className={styles.confirmBox} role="group" aria-label="Confirmar envio">
          <p className={styles.confirmText}>
            {reenvioRecente
              ? `${studentName || "Este aluno"} recebeu um relatório ${diasDesde === 0 ? "hoje" : `há ${diasDesde} dia${diasDesde === 1 ? "" : "s"}`}. Enviar de novo?`
              : `Enviar o relatório para ${studentName || "o aluno"}?`}
          </p>
          <div className={styles.confirmActions}>
            <button type="button" className={styles.btn} onClick={() => setConfirmando(false)} disabled={enviando}>
              Cancelar
            </button>
            <button
              ref={confirmarRef}
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={enviar}
              disabled={enviando}
            >
              {enviando ? "Enviando…" : "Confirmar envio"}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnBlock}`}
          onClick={() => setConfirmando(true)}
          disabled={!emailOk}
        >
          <FontAwesomeIcon icon={faPaperPlane} /> Enviar relatório
        </button>
      )}
    </div>
  );
}
